"use client";

import {
  Badge,
  Button,
  cn,
  DataTable,
  Input,
  type TanstackReactTable,
  toast,
} from "@devhop/ui";
import { Link, useParams } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FileTextIcon,
  LayoutGridIcon,
  ListIcon,
  PackageIcon,
  PhoneIcon,
  PrinterIcon,
  SearchIcon,
  UserIcon,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { hasShopFeature } from "@/modules/merchant/presentation/lib/has-shop-feature";
import { getStoredMerchantPosBranchId } from "@/modules/merchant/presentation/lib/merchant-pos-branch-storage";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { RequireMerchantPermission } from "@/modules/merchant/presentation/ui/RequireMerchantPermission";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import type { WorkshopJobListRow } from "../api/client";
import { workshopApi } from "../api/client";
import { useWorkshopJobsQuery } from "../api/queries";
import { printWorkshopQuote } from "../lib/print-workshop-quote";
import {
  formatWorkshopDateTime,
  formatWorkshopMoney,
  quoteStatusToneClass,
  statusToneClass,
  WORKSHOP_QUOTE_STATUS_FILTERS,
  WORKSHOP_QUOTE_STATUS_LABELS,
  WORKSHOP_STATUS_LABELS,
  type WorkshopQuoteStatusFilter,
} from "../lib/workshop-labels";
import { workshopToastError } from "../lib/workshop-user-messages";

function QuoteFilterChip({
  active,
  label,
  count,
  onClick,
}: {
  active: boolean;
  label: string;
  count: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex h-10 shrink-0 touch-manipulation items-center gap-1.5 rounded-full border px-3.5 font-medium text-sm transition",
        active
          ? "border-primary bg-primary text-primary-foreground shadow-sm"
          : "border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground",
      )}
    >
      {label}
      <span
        className={cn(
          "rounded-full px-1.5 py-0.5 font-bold text-xs tabular-nums",
          active ? "bg-primary-foreground/20" : "bg-muted text-foreground",
        )}
      >
        {count}
      </span>
    </button>
  );
}

function QuotePrintButtons({
  shopId,
  jobId,
  disabled,
  shopName,
  branchName,
}: {
  shopId: string;
  jobId: string;
  disabled: boolean;
  shopName?: string;
  branchName?: string | null;
}) {
  const [loading, setLoading] = useState(false);

  async function handlePrint(e: React.MouseEvent, paper: "thermal80" | "a4") {
    e.preventDefault();
    e.stopPropagation();
    if (disabled || loading) return;
    setLoading(true);
    try {
      const [jobDetail, quote] = await Promise.all([
        workshopApi.getJob(shopId, jobId),
        workshopApi.getQuote(shopId, jobId),
      ]);
      printWorkshopQuote(
        jobDetail,
        quote,
        shopName ? { shopName, branchName: branchName ?? null } : undefined,
        paper,
      );
    } catch (err) {
      toast.error(workshopToastError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex gap-1.5">
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="h-8 bg-background"
        disabled={disabled || loading}
        onClick={(e) => void handlePrint(e, "thermal80")}
      >
        <PrinterIcon className="size-3.5" />
        80
      </Button>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="h-8 bg-background"
        disabled={disabled || loading}
        onClick={(e) => void handlePrint(e, "a4")}
      >
        <PrinterIcon className="size-3.5" />
        A4
      </Button>
    </div>
  );
}

function QuoteCard({
  shopId,
  job,
  shopName,
  branchName,
}: {
  shopId: string;
  job: WorkshopJobListRow;
  shopName?: string;
  branchName?: string | null;
}) {
  const summary = job.quoteSummary;
  const emptyQuote =
    !summary || (summary.partsCount === 0 && summary.laborsCount === 0);
  return (
    <Link
      to="/app/shops/$shopId/workshop/jobs/$jobId"
      params={{ shopId, jobId: job.id }}
      className={cn(
        "block rounded-2xl border border-border bg-card p-3.5 text-left shadow-sm transition",
        "touch-manipulation hover:border-primary/40 hover:shadow-md active:scale-[0.99]",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-bold text-xl tracking-wide">{job.plateNumber}</p>
          <p className="mt-0.5 font-mono text-muted-foreground text-xs">
            {job.jobNumber} · {formatWorkshopDateTime(job.updatedAt)}
          </p>
        </div>
        <Badge
          className={cn(
            "shrink-0 border px-2.5 py-1 text-[11px]",
            quoteStatusToneClass(job.quoteStatus),
          )}
          variant="secondary"
        >
          {WORKSHOP_QUOTE_STATUS_LABELS[
            job.quoteStatus as keyof typeof WORKSHOP_QUOTE_STATUS_LABELS
          ] ?? job.quoteStatus}
        </Badge>
      </div>

      <div className="mt-3 flex items-center gap-2 text-sm">
        <UserIcon className="size-3.5 shrink-0 text-muted-foreground" />
        <span className="truncate font-medium">{job.customerName}</span>
        <span className="shrink-0 text-muted-foreground">·</span>
        <PhoneIcon className="size-3.5 shrink-0 text-muted-foreground" />
        <span className="truncate text-muted-foreground text-xs">
          {job.customerPhone}
        </span>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-border border-t border-dashed pt-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge
            className={cn(
              "border px-2 py-0.5 text-[10px]",
              statusToneClass(job.status),
            )}
            variant="secondary"
          >
            {WORKSHOP_STATUS_LABELS[job.status]}
          </Badge>
          {summary ? (
            <span className="inline-flex items-center gap-1 text-muted-foreground text-xs">
              <PackageIcon className="size-3" />
              {summary.partsCount} ອາໄຫຼ່ · {summary.laborsCount} ຄ່າແຮງ
            </span>
          ) : null}
        </div>
        <p className="font-bold text-base tabular-nums">
          {summary && !emptyQuote ? (
            formatWorkshopMoney(summary.grandTotal, job.currency)
          ) : (
            <span className="text-muted-foreground text-sm">ຍັງບໍ່ມີລາຍການ</span>
          )}
        </p>
      </div>

      {!emptyQuote ? (
        // Print buttons stopPropagation/preventDefault themselves, so the Link
        // won't navigate when they're clicked — no wrapper handler needed.
        <div className="mt-2">
          <QuotePrintButtons
            shopId={shopId}
            jobId={job.id}
            disabled={emptyQuote}
            shopName={shopName}
            branchName={branchName}
          />
        </div>
      ) : null}
    </Link>
  );
}

/** Makes a table cell navigate to the job (whole row becomes clickable). */
function JobCellLink({
  shopId,
  jobId,
  className,
  children,
}: {
  shopId: string | undefined;
  jobId: string;
  className?: string;
  children: React.ReactNode;
}) {
  if (!shopId) return <>{children}</>;
  return (
    <Link
      to="/app/shops/$shopId/workshop/jobs/$jobId"
      params={{ shopId, jobId }}
      className={cn("block", className)}
    >
      {children}
    </Link>
  );
}

export function ShopWorkshopQuotesPage() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const { currentShop } = useMerchantShops(shopId);

  const [quoteFilter, setQuoteFilter] =
    useState<WorkshopQuoteStatusFilter>("all");
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(20);
  const [viewMode, setViewMode] = useState<"card" | "table">("card");

  const listQuery = useMemo(() => {
    const branchId =
      (shopId && getStoredMerchantPosBranchId(shopId)) ||
      currentShop?.branchId ||
      undefined;
    const filterBranch =
      hasShopFeature(currentShop?.featureFlags, "multi_branch") && branchId
        ? branchId
        : undefined;
    return {
      listKind: "quotes" as const,
      status: "all" as const,
      quoteStatus: "all" as const,
      limit: 100,
      offset: 0,
      q: search.trim() || undefined,
      ...(filterBranch ? { branchId: filterBranch } : {}),
    };
  }, [search, shopId, currentShop?.branchId, currentShop?.featureFlags]);

  const jobsQ = useWorkshopJobsQuery(shopId, listQuery);
  const jobs = jobsQ.data?.data ?? [];

  const quoteCounts = useMemo(() => {
    const counts: Record<WorkshopQuoteStatusFilter, number> = {
      all: jobs.length,
      draft: 0,
      sent: 0,
      approved: 0,
      rejected: 0,
    };
    for (const job of jobs) {
      const key = job.quoteStatus as WorkshopQuoteStatusFilter;
      if (key in counts && key !== "all") counts[key] += 1;
    }
    return counts;
  }, [jobs]);

  const filteredJobs = useMemo(() => {
    if (quoteFilter === "all") return jobs;
    return jobs.filter((j) => j.quoteStatus === quoteFilter);
  }, [jobs, quoteFilter]);

  // Back to the first page whenever the filter / search changes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset intent
  useEffect(() => {
    setOffset(0);
  }, [quoteFilter, search]);

  const pagedJobs = useMemo(
    () => filteredJobs.slice(offset, offset + limit),
    [filteredJobs, offset, limit],
  );

  const columns: TanstackReactTable.ColumnDef<WorkshopJobListRow>[] = useMemo(
    () => [
      {
        id: "plate",
        header: "ທະບຽນ",
        size: 120,
        cell: ({ row }) => (
          <JobCellLink
            shopId={shopId}
            jobId={row.original.id}
            className="font-bold hover:text-primary"
          >
            {row.original.plateNumber}
          </JobCellLink>
        ),
      },
      {
        id: "jobNumber",
        header: "ເລກໃບ",
        size: 110,
        cell: ({ row }) => (
          <JobCellLink
            shopId={shopId}
            jobId={row.original.id}
            className="font-mono text-muted-foreground text-xs"
          >
            {row.original.jobNumber}
          </JobCellLink>
        ),
      },
      {
        id: "customer",
        header: "ລູກຄ້າ",
        size: 170,
        cell: ({ row }) => (
          <JobCellLink
            shopId={shopId}
            jobId={row.original.id}
            className="min-w-0"
          >
            <div className="truncate font-medium">
              {row.original.customerName}
            </div>
            <div className="truncate text-muted-foreground text-xs">
              {row.original.customerPhone}
            </div>
          </JobCellLink>
        ),
      },
      {
        id: "date",
        header: "ວັນທີ",
        size: 150,
        cell: ({ row }) => (
          <JobCellLink
            shopId={shopId}
            jobId={row.original.id}
            className="text-muted-foreground text-xs"
          >
            {formatWorkshopDateTime(row.original.updatedAt)}
          </JobCellLink>
        ),
      },
      {
        id: "status",
        header: "ສະຖານະ",
        size: 110,
        cell: ({ row }) => {
          const r = row.original;
          return (
            <JobCellLink shopId={shopId} jobId={r.id}>
              <Badge
                className={cn(
                  "border px-2 py-0.5 text-[11px]",
                  quoteStatusToneClass(r.quoteStatus),
                )}
                variant="secondary"
              >
                {WORKSHOP_QUOTE_STATUS_LABELS[
                  r.quoteStatus as keyof typeof WORKSHOP_QUOTE_STATUS_LABELS
                ] ?? r.quoteStatus}
              </Badge>
            </JobCellLink>
          );
        },
      },
      {
        id: "total",
        header: "ຍອດລວມ",
        size: 120,
        cell: ({ row }) => {
          const r = row.original;
          const s = r.quoteSummary;
          const empty = !s || (s.partsCount === 0 && s.laborsCount === 0);
          return (
            <JobCellLink shopId={shopId} jobId={r.id}>
              {empty ? (
                <span className="text-muted-foreground text-sm">—</span>
              ) : (
                <span className="font-bold tabular-nums">
                  {formatWorkshopMoney(s.grandTotal, r.currency)}
                </span>
              )}
            </JobCellLink>
          );
        },
      },
      {
        id: "actions",
        size: 130,
        cell: ({ row }) => {
          const r = row.original;
          const s = r.quoteSummary;
          const empty = !s || (s.partsCount === 0 && s.laborsCount === 0);
          if (!shopId || empty) return null;
          return (
            <QuotePrintButtons
              shopId={shopId}
              jobId={r.id}
              disabled={empty}
              shopName={currentShop?.shopName}
              branchName={currentShop?.branchName}
            />
          );
        },
      },
    ],
    [shopId, currentShop?.shopName, currentShop?.branchName],
  );

  return (
    <RequireMerchantPermission permission={MerchantPermissions.workshop.read}>
      <Header>
        <Breadcrumb items={shopBreadcrumbs.workshopQuotes(shopId)} />
      </Header>
      <Main className="pb-8">
        <div className="flex flex-col gap-4 py-2 md:py-4">
          <section className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
            {shopId ? (
              <div className="border-border border-b px-4 py-2">
                <Link
                  to="/app/shops/$shopId/workshop"
                  params={{ shopId }}
                  className="inline-flex touch-manipulation items-center gap-1.5 text-muted-foreground text-sm transition-colors hover:text-foreground"
                >
                  <ArrowLeftIcon className="size-4" />
                  ກັບກະດານວຽກ
                </Link>
              </div>
            ) : null}

            <div className="px-4 py-4">
              <div className="flex items-center gap-2.5">
                <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-violet-500/10 text-violet-700">
                  <FileTextIcon className="size-5" />
                </span>
                <div className="min-w-0">
                  <h1 className="font-bold text-xl tracking-tight md:text-2xl">
                    ໃບປະເມີນລາຄາ
                  </h1>
                  <p className="text-muted-foreground text-xs md:text-sm">
                    ລາຍການໃບສະເໜີຈາກໃບງານສ້ອມ
                    {currentShop?.shopName
                      ? ` · ${currentShop.shopName}`
                      : null}
                  </p>
                </div>
              </div>
            </div>
          </section>

          <div className="relative max-w-md">
            <SearchIcon className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="ຄົ້ນທະເບຽນ / ເລກໃບງານ / ຊື່ / ເບີໂທ…"
              className="h-11 rounded-xl pl-9"
            />
          </div>

          <div className="flex items-center justify-between gap-2">
            <div className="-mx-1 flex min-w-0 flex-1 gap-2 overflow-x-auto px-1 pb-1">
              {WORKSHOP_QUOTE_STATUS_FILTERS.map((status) => (
                <QuoteFilterChip
                  key={status}
                  active={quoteFilter === status}
                  label={
                    status === "all"
                      ? "ທັງໝົດ"
                      : WORKSHOP_QUOTE_STATUS_LABELS[status]
                  }
                  count={quoteCounts[status]}
                  onClick={() => setQuoteFilter(status)}
                />
              ))}
            </div>
            {/* ສະຫຼັບມຸມມອງ: ການ໌ດ ↔ ຕາຕະລາງ */}
            <div className="inline-flex shrink-0 rounded-lg border border-border p-0.5">
              <button
                type="button"
                aria-label="ມຸມມອງການ໌ດ"
                aria-pressed={viewMode === "card"}
                onClick={() => setViewMode("card")}
                className={cn(
                  "flex items-center justify-center rounded-md p-1.5",
                  viewMode === "card"
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                <LayoutGridIcon className="size-4" />
              </button>
              <button
                type="button"
                aria-label="ມຸມມອງຕາຕະລາງ"
                aria-pressed={viewMode === "table"}
                onClick={() => setViewMode("table")}
                className={cn(
                  "flex items-center justify-center rounded-md p-1.5",
                  viewMode === "table"
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                <ListIcon className="size-4" />
              </button>
            </div>
          </div>

          {jobsQ.isError ? (
            <div className="rounded-2xl border border-destructive/30 bg-destructive/5 px-4 py-8 text-center">
              <p className="font-medium text-destructive text-sm">
                ໂຫຼດລາຍການບໍ່ໄດ້
              </p>
            </div>
          ) : viewMode === "card" ? (
            <>
              {jobsQ.isLoading ? (
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {["a", "b", "c", "d", "e", "f"].map((k) => (
                    <div
                      key={k}
                      className="h-36 animate-pulse rounded-2xl bg-muted"
                    />
                  ))}
                </div>
              ) : filteredJobs.length === 0 ? (
                <div className="rounded-2xl border border-border border-dashed bg-muted/20 px-4 py-12 text-center">
                  <FileTextIcon className="mx-auto size-10 text-muted-foreground/50" />
                  <p className="mt-3 font-medium text-sm">ບໍ່ພົບໃບປະເມີນ</p>
                </div>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {pagedJobs.map((job) =>
                    shopId ? (
                      <QuoteCard
                        key={job.id}
                        shopId={shopId}
                        job={job}
                        shopName={currentShop?.shopName}
                        branchName={currentShop?.branchName}
                      />
                    ) : null,
                  )}
                </div>
              )}
              {filteredJobs.length > 0 ? (
                <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
                  <p className="text-muted-foreground text-xs tabular-nums">
                    ສະແດງ {offset + 1}–
                    {Math.min(offset + limit, filteredJobs.length)} ຈາກ{" "}
                    {filteredJobs.length}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-8"
                      disabled={offset <= 0}
                      onClick={() => setOffset(Math.max(0, offset - limit))}
                    >
                      <ChevronLeftIcon className="size-4" />
                    </Button>
                    <span className="text-sm tabular-nums">
                      {Math.floor(offset / limit) + 1}/
                      {Math.max(1, Math.ceil(filteredJobs.length / limit))}
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-8"
                      disabled={offset + limit >= filteredJobs.length}
                      onClick={() => setOffset(offset + limit)}
                    >
                      <ChevronRightIcon className="size-4" />
                    </Button>
                  </div>
                </div>
              ) : null}
            </>
          ) : (
            <DataTable<WorkshopJobListRow, unknown>
              noDataMessage="ບໍ່ພົບໃບປະເມີນ — ເພີ່ມອາໄຫຼ່/ຄ່າແຮງໃນໃບງານກ່ອນ"
              isLoading={jobsQ.isLoading}
              columns={columns}
              data={pagedJobs}
              offset={offset}
              limit={limit}
              totalCount={filteredJobs.length}
              onPaginationChange={(p) => {
                setOffset(p.offset);
                setLimit(p.limit);
              }}
            />
          )}
        </div>
      </Main>
    </RequireMerchantPermission>
  );
}
